import axios from 'axios';
import debounce from 'debounce';
import { reject, equals } from 'ramda';
import { getPath } from './helpers';
import {
  moodboardPath,
  categoryPath,
  copyCategoryPath,
  SEARCH_AUTOCOMPLETE_PATH,
  SEARCH_PRODUCTS_PATH,
  TAGS_AUTOCOMPLETE_PATH,
  FAVOURITE_PRODUCT_PATH,
  FAVOURITE_MOODBOARD_CATEGORIES_PATH,
  FAVOURITE_MOODBOARD_TEMPLATES_PATH,
} from './routing';
import { cancel } from '../../common/utils/request';

const prepareQueryString = (value, excludedWords) => {
  const ex = reject(equals(''))(excludedWords.split(/\s+/g));
  return `${value} ${ex.length > 0 ? `!${ex.join(' !')}` : ''}`.trim();
};

const autocompleteProc = debounce(
  (
    search,
    excludedWords,
    tags,
    minPrice,
    maxPrice,
    variantId,
    moodboardId,
    shopThroughBasketOnly,
    removeManual,
    onSuccess,
    onError,
  ) => {
    axios
      .get(
        getPath(SEARCH_AUTOCOMPLETE_PATH, {
          search: prepareQueryString(search, excludedWords),
          tags,
          minPrice: encodeURIComponent(minPrice),
          maxPrice: encodeURIComponent(maxPrice),
          variantId,
          moodboardId,
          shopThroughBasketOnly,
          removeManual,
        }),
      )
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },
  500,
);

const searchProc = debounce(
  (
    tags,
    value,
    excludedWords,
    minPrice,
    maxPrice,
    variantId,
    moodboardId,
    excludedSuppliers,
    includedSuppliers,
    favourite,
    shopThroughBasketOnly,
    removeManual,
    supplierTerm,
    bestSupplier,
    directTrade,
    wholesaleSupplier,
    page,
    onSuccess,
    onError,
  ) => {
    cancel.getMethod('searchProc')();

    axios
      .get(
        getPath(SEARCH_PRODUCTS_PATH, {
          tags,
          value: prepareQueryString(value, excludedWords),
          minPrice,
          maxPrice,
          variantId,
          moodboardId,
          excluded_supplier_ids: excludedSuppliers,
          included_supplier_ids: includedSuppliers,
          favourite,
          shop_through_basket_only: shopThroughBasketOnly,
          remove_manual: removeManual,
          supplier_term: supplierTerm ? 'trade' : '',
          best_supplier: bestSupplier,
          direct_trade: directTrade,
          wholesale_supplier: wholesaleSupplier,
          page,
        }),
        {
          cancelToken: new axios.CancelToken(c => {
            cancel.setMethod('searchProc', c);
          }),
        },
      )
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },
  500,
);

const updateMoodboardProc = debounce((attrs, onSuccess, onError) => {
  axios
    .put(moodboardPath(window.moodboardId), attrs)
    .then(response => {
      onSuccess(response.data);
    })
    .catch(onError);
}, 500);

const tagsAutocompleteProc = debounce((search, tags, onSuccess, onError) => {
  axios
    .get(getPath(TAGS_AUTOCOMPLETE_PATH, { search, tags }))
    .then(response => {
      onSuccess(response.data);
    })
    .catch(onError);
}, 500);

const serializeTags = tags =>
  JSON.stringify(tags.map(i => ({ id: encodeURIComponent(i.id), type: i.type })));
const serializeSuppliers = suppliers =>
  JSON.stringify(suppliers.map(i => encodeURIComponent(i.id)));

export const MoodboardApi = {
  searchAutocomplete: (
    search,
    excludedWords,
    tags,
    minPrice,
    maxPrice,
    variantId,
    moodboardId,
    shopThroughBasketOnly,
    removeManual,
    onSuccess,
    onError,
  ) => {
    autocompleteProc(
      search,
      excludedWords,
      serializeTags(tags),
      minPrice,
      maxPrice,
      variantId,
      moodboardId,
      shopThroughBasketOnly,
      removeManual,
      onSuccess,
      onError,
    );
  },

  searchProducts: (
    tags,
    value,
    excludedWords,
    minPrice,
    maxPrice,
    variantId,
    moodboardId,
    excludedSuppliers,
    includedSuppliers,
    favourite,
    shopThroughBasketOnly,
    removeManual,
    supplierTerm,
    bestSupplier,
    directTrade,
    wholesaleSupplier,
    page,
    onSuccess,
    onError,
  ) => {
    searchProc(
      serializeTags(tags),
      value,
      excludedWords,
      minPrice,
      maxPrice,
      variantId,
      moodboardId,
      serializeSuppliers(excludedSuppliers),
      serializeSuppliers(includedSuppliers),
      favourite,
      shopThroughBasketOnly,
      removeManual,
      supplierTerm,
      bestSupplier,
      directTrade,
      wholesaleSupplier,
      page,
      onSuccess,
      onError,
    );
  },

  tagsAutocomplete: (search, tags, onSuccess, onError) => {
    tagsAutocompleteProc(search, serializeTags(tags), onSuccess, onError);
  },

  updateMoodboard: (attrs, onSuccess, onError) => {
    updateMoodboardProc(attrs, onSuccess, onError);
  },

  updateRoomType: (roomType, onSuccess, onError) => {
    axios
      .put(`${moodboardPath(window.moodboardId)}/update_room_type`, { room_type: roomType })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  startProject: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/start`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  completeConsultancyProject: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/consultancy_complete`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  sendToReview: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/send_to_review`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  approve: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/approve`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  createPublicMoodboard: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/create_public_moodboard`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  publish: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/publish`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  unpublish: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/unpublish`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  sendBackToDesigner: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/send_back_to_designer`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  categoryReleaseApprove: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/category_release_approve`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  categoryReleaseRequest: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/category_release_request`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  categoryReleaseReject: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/category_release_reject`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  additionalHoursApprove: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/additional_hours_approve`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  additionalHoursReject: (onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/additional_hours_reject`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  checkStatus: (onSuccess, onError) => {
    if (window.moodboardId) {
      axios
        .get(`${moodboardPath(window.moodboardId)}/status`)
        .then(response => {
          onSuccess(response.data);
        })
        .catch(onError);
    }
  },

  uploadHeroImage: (file, position, onSuccess, onError) => {
    const formData = new FormData();
    formData.append('image[file]', file);
    formData.append('image[position]', position);
    axios
      .post(`${moodboardPath(window.moodboardId)}/hero_images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  updateHeroImagesPositions: (images, onSuccess, onError) => {
    const clearedImages = images.map(image => ({ id: image.id, type: image.type }));
    axios
      .post(`${moodboardPath(window.moodboardId)}/hero_images/update_positions`, {
        images: clearedImages,
      })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  toggleHeroImagesCategoryRelease: (image, onSuccess, onError) => {
    axios
      .post(
        `${moodboardPath(window.moodboardId)}/${
          image.type === 'Video' ? 'videos' : 'hero_images'
        }/${image.id}/toggle_category_release`,
        {},
      )
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  uploadPresentation: (video, onSuccess, onError) => {
    axios
      .post(`${moodboardPath(window.moodboardId)}/videos`, { video })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  removePresentation: (videoId, onSuccess, onError) => {
    axios
      .delete(`${moodboardPath(window.moodboardId)}/videos/${videoId}`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  updateHeroPresentation: (video, onSuccess, onError) => {
    axios
      .put(`${moodboardPath(window.moodboardId)}/videos/${video.id}`, { video })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  removeHeroImage: (imageId, onSuccess, onError) => {
    axios
      .delete(`${moodboardPath(window.moodboardId)}/hero_images/${imageId}`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  updateHeroImage: (image, onSuccess, onError) => {
    axios
      .put(`${moodboardPath(window.moodboardId)}/hero_images/${image.id}`, { image })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadProductCategories: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath()}/taxons`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadSuppliers: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath()}/suppliers`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },
  searchSuppliers: (query, onSuccess, onError) => {
    axios
      .get(getPath(`${moodboardPath()}/suppliers`, { query }))
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadBrands: (search, onSuccess, onError) => {
    axios
      .get(getPath(`${moodboardPath()}/brands`, { search }))
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  createCustomProduct: (attrs, images, onSuccess, onError) => {
    const formData = new FormData();
    ['title', 'link'].forEach(field => {
      formData.append(`product[${field}]`, attrs[field]);
    });
    images.forEach(image => formData.append('images[]', image));

    axios
      .post(`${moodboardPath(window.moodboardId)}/custom_products`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  removeCustomProduct: (productId, onSuccess, onError) => {
    axios
      .delete(`${moodboardPath(window.moodboardId)}/custom_products/${productId}`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  createProduct: (attrs, images, onSuccess, onError) => {
    const formData = new FormData();
    [
      'title',
      'keywords_text',
      'link',
      'sku',
      'description',
      'taxon_id',
      'supplier_id',
      'brand_text',
      'purchase_through_designer',
      'stock_expected_on',
    ].forEach(field => {
      formData.append(`product[${field}]`, attrs[field]);
    });
    ['sku', 'price', 'sale_price', 'dimensions'].forEach(field => {
      formData.append(`variant[${field}]`, attrs[field]);
    });

    images.forEach(image => formData.append('images[]', image));

    axios
      .post(`${moodboardPath()}/products`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  addProduct: (categoryId, list, product, position, onSuccess, onError) => {
    axios
      .post(`${categoryPath(categoryId)}/variants`, { list, position, variant: product })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  removeProduct: (categoryId, list, product, onSuccess, onError) => {
    axios
      .delete(`${categoryPath(categoryId)}/variants/${product.id}`, { data: { list } })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  updateMoodboardProduct: (categoryId, product, onSuccess, onError) => {
    axios
      .put(`${categoryPath(categoryId)}/variants/${product.id}`, { moodboard_variant: product })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  updateCategory: (categoryId, attrs, onSuccess, onError) => {
    axios
      .put(categoryPath(categoryId), { category: attrs })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  moveCategory: (categoryId, column, position, onSuccess, onError) => {
    axios
      .put(`${categoryPath(categoryId)}/move`, { column, position })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  createCategory: (product, category, onSuccess, onError) => {
    axios
      .post(categoryPath(), { variant: product, category })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  removeCategory: (categoryId, onSuccess, onError) => {
    axios
      .delete(categoryPath(categoryId))
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  toggleCategoryFavourite: (categoryId, favourite, onSuccess, onError) => {
    const path = `${FAVOURITE_MOODBOARD_CATEGORIES_PATH}/${categoryId}`;
    const promise = favourite ? axios.post(path, {}) : axios.delete(path, {});
    promise
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  setProductFavorite: variant => {
    const path = FAVOURITE_PRODUCT_PATH.replace(':product_id', variant.product_id);

    if (variant.favourite) {
      axios.post(path, {});
    } else {
      axios.put(path, {});
    }
  },

  loadCustomerFavouriteProductsItems: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/customer_favourite_products`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadCustomerBasketItems: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/customer_basket`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadCustomerOrders: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/customer_orders`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadCustomerFavourites: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/customer_favourites`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  loadCustomerProjects: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/customer_projects`)
      .then(response => {
        onSuccess(response);
      })
      .catch(onError);
  },

  copyCategory: (targetMoodboardId, categoryId, params, onSuccess, onError) => {
    axios
      .post(`${copyCategoryPath(targetMoodboardId)}`, { category: { ...params, id: categoryId } })
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  getFavouriteCategories: (onSuccess, onError) => {
    axios
      .get(FAVOURITE_MOODBOARD_CATEGORIES_PATH)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  getCategoryTemplatesMoodboards: (onSuccess, onError) => {
    axios
      .get(FAVOURITE_MOODBOARD_TEMPLATES_PATH)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },

  getDesignTotals: (onSuccess, onError) => {
    axios
      .get(`${moodboardPath(window.moodboardId)}/design_totals`)
      .then(response => {
        onSuccess(response.data);
      })
      .catch(onError);
  },
};



// WEBPACK FOOTER //
// ./app/javascript/design/api/moodboard-api.js